import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import {
  P,
  B,
  SubHead,
  UList,
  OList,
  TextLink,
  GroupLabel,
} from 'app/components/StaticPages';
import { CONTACT_EMAIL } from 'app/core/constants';

/** Legal paragraph — agreement copy uses a wider bottom margin. */
const LP = styled(P)(({ theme }) => ({ marginBottom: theme.spacing(2) }));

/** Bold lead-in line before a list. */
const Lead = styled(P)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.staticPages.text.body,
  fontWeight: 600,
}));

/** Uppercase mini-label for sub-blocks (Disney vs non-Disney returns). */
const SubLabel = styled(GroupLabel)(({ theme }) => ({
  color: theme.palette.staticPages.navy,
  marginBottom: theme.spacing(1),
}));

/** Numbered bold sub-heading inside the privacy policy. */
const PrivacyHead = styled(SubHead)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export interface AgreementSection {
  id: string;
  toc: string;
  heading: string;
  body: ReactNode;
}

export const AGREEMENT_INTRO = (
  <P sx={{ m: 0, color: 'staticPages.text.story' }}>
    This Agreement is between you, the “Client,” and Kingdom Strollers, LLC
    (“Kingdom”). Kingdom remains the sole owner of all of its rental equipment.
    Kingdom is not offering for sale, and the Client is not purchasing, the
    equipment. The Client hereby acknowledges that the equipment shall be for
    personal use only. By accepting the terms and conditions of this rental
    agreement, the Client acknowledges that they have read, understood, and
    agree to the terms stated below.
  </P>
);

export const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    id: 'sec-reservations',
    toc: 'Reservations & Payment',
    heading: 'Reservations & Payment of Rental Fees',
    body: (
      <>
        <LP>
          The Client&apos;s reservation is attached hereto and incorporated by
          reference.
        </LP>
        <LP>
          Payment is due by credit card, debit card, or PayPal at the time of
          making the reservation. The Client agrees to allow Kingdom to make
          alterations, adjustments, and additions to the form of payment for the
          purposes of crediting, refunding, and charging for any service or fee
          associated with their reservation.
        </LP>
        <LP>
          Kingdom will confirm your reservation by email within 24 hours. The
          Client should contact Kingdom if a confirmation email is not received
          within 24 hours of placing a reservation online or over the phone.
        </LP>
        <LP>The colors of products may vary from the images shown.</LP>
      </>
    ),
  },
  {
    id: 'sec-delivery',
    toc: 'Delivery, Possession & Usage',
    heading: 'Delivery, Possession & Usage',
    body: (
      <>
        <LP>
          At the time of reservation, the Client will specify the name and
          location of the Orlando theme-park-area resort, hotel, or vacation
          home where the rental item(s) will be delivered and retrieved. The
          Client understands that delivery / return pick-up locations and
          procedures may vary based on their chosen location. The exact time and
          location of both delivery and the return pick-up will be communicated
          via email to the Client. If the Client is unsure as to the location of
          the delivery or the return pick-up, the Client agrees to contact
          Kingdom to clarify. The Client understands that delivery and return
          are based on addresses in the immediate Orlando theme-park area in the
          cities of Orlando, Kissimmee, Clermont, Davenport, and Winter Garden.
          Addresses outside of these cities are not serviced by Kingdom
          Strollers.
        </LP>
        <LP>
          The Client agrees to contact Kingdom within 6 hours of the agreed
          delivery / return pick-up date and time, by phone or email, if the
          Client is unable to locate or take delivery of the rental item(s) when
          the agreed-upon location is a non-Disney resort. The Client agrees to
          contact Kingdom within one hour of the agreed-upon delivery / return
          pick-up date and time, by phone or email, if the Client is unable to
          meet with a representative from Kingdom Strollers for the personal
          delivery / return pick-up of their rented item at an agreed-upon
          Disney-owned resort. If the Client is late, misses their delivery /
          return pick-up time, or is otherwise unavailable to meet a
          representative at the agreed-upon time, the Client may (at
          Kingdom&apos;s sole discretion) request the next available meeting
          time for their designated resort. The Client agrees and understands
          that delivery and return pick-up at a Disney-owned resort will be made
          face to face with a representative from Kingdom Strollers, and agrees
          to select an available time in which they will be available to meet
          face to face. The Client also agrees and understands that only the
          times listed at www.KingdomStrollers.com are available for their
          chosen delivery / pick-up locations, and that other times are not
          available unless specific exceptions and arrangements are made through
          Kingdom Strollers via email.
        </LP>
        <LP>
          The Client agrees and understands that all requests for changes to the
          existing reservation must be made at least 24 hours before the agreed
          delivery time in said reservation. Change requests made less than 24
          hours before the agreed delivery time will be honored at the
          discretion of Kingdom.
        </LP>
        <LP>
          The Client agrees and understands that all deliveries are made with
          earnest effort to the requested delivery time but, due to traffic or
          volume, there is an allowable 30-minute window around the requested
          delivery time. The Client understands and agrees that during times of
          extreme weather, deliveries and/or pick-ups may be temporarily
          suspended for the safety of delivery personnel until it is deemed safe
          by management to continue operations (extreme weather may include, but
          is not limited to, tropical storms / hurricanes, tornado warnings, and
          winds exceeding 25 mph).
        </LP>
        <LP>
          The Client agrees that if no contact is made within 24 hours of the
          agreed delivery date, the Client accepts possession of the rental
          item(s) and their responsibilities during possession.
        </LP>
        <LP>
          If an attempt is made to deliver rental items to a vacation home and
          the Client is unavailable, or the Client has provided the incorrect
          delivery location resulting in a 2nd trip to redeliver, a $15
          re-delivery fee may be applied at Kingdom&apos;s discretion.
        </LP>
        <LP>
          The Client agrees that they have made every effort to verify that
          their desired delivery and return locations are within the stated
          delivery area and that this location is serviced by Kingdom Strollers
          (as stated in the FAQ section at www.KingdomStrollers.com). Any
          reservation may be altered or canceled at any time at Kingdom&apos;s
          discretion if the desired location is not in the delivery area as
          stated. The Client understands that any rental item returned to a
          location outside our stated delivery area may incur a $125 fee at
          Kingdom&apos;s sole discretion. If the Client has traveled outside our
          delivery / pick-up area and is unable to return, it is the
          Client&apos;s responsibility to ensure that all rented items and
          associated items are returned to Kingdom on the original return date
          by means of taxi, rideshare, courier, or freight shipping (FedEx, UPS,
          USPS, etc.). All charges for any such return shall be the sole
          responsibility of the Client.
        </LP>
        <LP>
          The Client agrees that any reservation may be altered or canceled at
          Kingdom&apos;s discretion if Kingdom deems the desired hotel, resort,
          or vacation home&apos;s (delivery or pick-up location) health and
          safety standards to be detrimental to the nature of our business.
        </LP>
        <LP>
          The Client agrees to maintain possession of the rental item throughout
          the course of their rental period. Rental items are to be kept secure
          when not in use (hotel rooms, vehicles). Items are not to be left with
          bell stands or luggage rooms for daily storage. Items found with bell
          stands or luggage rooms may be considered abandoned or returned early
          and are subject to pick-up. Items left with bell stands or luggage
          rooms are not covered by the optional insurance purchase.
        </LP>
        <LP>
          The Client agrees to inform Kingdom immediately of any defect or
          malfunction while in use. Once notified, Kingdom will make every
          effort to remedy the issue in a timely manner.
        </LP>
        <LP>
          The Client agrees to only use the rental item for its intended purpose
          in a manner for which it was designed. At no time should the rental
          item&apos;s stated weight limits (as stated on this site) be exceeded.
        </LP>
        <LP>
          The Client agrees that violations of this agreement may result in the
          termination of the rental. Kingdom reserves the right to terminate a
          rental based on evidence of abuse or violation of any and all parts of
          this agreement. The Client agrees to surrender any and all rented
          equipment and accessories to Kingdom upon request. Kingdom may, at its
          sole discretion, refund any portion of a rental that it deems
          necessary to terminate.
        </LP>
        <LP>
          The Client agrees that, based on the request of the Client, any
          stroller exchange due to (but not limited to) a stroller left in rain,
          child vomit, leaking diaper, or other bodily fluid may be charged a
          $10 exchange fee at Kingdom&apos;s discretion.
        </LP>
      </>
    ),
  },
  {
    id: 'sec-returning',
    toc: 'Returning Equipment',
    heading: 'Returning Equipment',
    body: (
      <>
        <SubLabel>
          Disney-Owned Resorts (in-person, face-to-face meet-up)
        </SubLabel>
        <LP>
          It is the responsibility of the Client to be available at the
          agreed-upon time and location for the return pick-up. The Client
          agrees NOT to leave strollers or ECV scooters with bell services, as
          this will automatically incur a charge of $75 per item and is
          considered abandonment and against Disney&apos;s policy. The return
          pick-up must be made face to face with a representative of Kingdom
          Strollers. If the Client is unable to meet at the agreed-upon time and
          location, it is the Client&apos;s responsibility to contact Kingdom
          Strollers to work out another option — rescheduling the return for the
          next available time slot or date, another physical location, or our
          Orlando airport kiosk.
        </LP>
        <SubLabel>Non-Disney Resorts or Hotels</SubLabel>
        <LP>
          It is the responsibility of the Client to ensure that all rental items
          are available for pick-up at the designated resort, hotel, or vacation
          home on the date and time specified in the reservation. All rental
          items are to be returned to the bell stand or registration desk at the
          resort or hotel listed in the confirmation email. At no time should
          any rental item ever be left in the guest&apos;s room after check-out
          — rental items left in rooms will incur a $25 recovery fee. The Client
          is 100% responsible for the items being available at the bell stand or
          front desk for pick-up. For rental items being returned from vacation
          homes, we prefer someone be available for pick-up at the vacation
          home. (Please note: if pick-up is scheduled for a morning return,
          pick-up may occur from 7am to 10am, depending on the location,
          additional-area pick-ups, and driver availability.) The Client agrees
          to contact Kingdom in the event that the rental item(s) are returned
          earlier than the agreed-upon date of return. Failure to notify Kingdom
          will void any insurance taken on the stroller.
        </LP>
        <LP>
          If the Client wishes to extend the rental period, the Client must
          contact Kingdom before the agreed-upon return time. Kingdom may, at
          its own discretion, allow or deny any extension. Kingdom reserves the
          right to charge the Client&apos;s card, and the Client agrees to allow
          the charge of $10 for each additional day or fraction of a day the
          rental equipment is kept by the Client.
        </LP>
        <LP>
          The Client agrees to contact Kingdom in the event of a delay in
          returning rental item(s) at the agreed-upon time. It is the sole
          discretion of Kingdom as to whether a grace period may be granted. A
          $20 fee may be applied for return trips to pick up rental items if not
          returned at the agreed-upon time as listed in the rental reservation
          or prearranged with Kingdom.
        </LP>
        <LP>
          The Client may request a change of product for reasons of size or
          transportation issues. These requests are solely at the discretion of
          Kingdom based on product availability, and are subject to a $10 fee
          per item.
        </LP>
      </>
    ),
  },
  {
    id: 'sec-damages',
    toc: 'Damages & Loss',
    heading: 'Damages to or Loss of Equipment',
    body: (
      <>
        <LP>
          The Client shall accept for use, as is, the rental item(s) listed in
          the reservation, and accepts full responsibility for the care of the
          equipment while it is in the Client&apos;s possession.
        </LP>
        <LP>
          The Client is responsible for paying the full replacement value of any
          rental item(s) rented under this Agreement that are not returned to
          Kingdom within 48 hours of the end of the agreement. The Client also
          agrees to authorize the replacement cost of each rental item to the
          Client&apos;s credit card in the event that the rental item(s) are not
          returned. If an item is recovered after the Client has been charged, a
          full refund of the replacement charge will be issued to the Client.
        </LP>
        <LP>
          The Client is responsible for paying the replacement cost of any lost
          parts or accessories, whether paid for or provided free of charge.
          These include but are not limited to shoulder straps, harnesses,
          parent consoles, rain covers, cooler bags, glider boards, and car seat
          adapters. These items may be provided for a small fee or free of
          charge and are not guaranteed to be available during your rental.
        </LP>
        <LP>
          The Client is responsible for the loss, theft, damage, or destruction
          of the rental item(s) and agrees to pay the full replacement value.
          The Client also agrees to authorize the replacement cost of the rental
          item(s) to the Client&apos;s credit card.
        </LP>
        <LP>
          The Client agrees to return the rental item(s) in the same condition
          as they were delivered, and to report any loss, theft, damage, or
          destruction to the rental item(s) immediately to Kingdom.
        </LP>
        <LP>
          The Client hereby acknowledges that they were offered the opportunity
          to purchase insurance to cover the theft, damage, or destruction of
          the rental item(s), and elected to purchase or declined to purchase
          insurance.
        </LP>
      </>
    ),
  },
  {
    id: 'sec-insurance',
    toc: 'Insurance Waiver',
    heading: 'Insurance Waiver Terms & Definitions',
    body: (
      <>
        <LP>
          Insurance covering theft, destruction, or accidental damage is
          available to purchase prior to the finalization of this agreement and
          any time before taking possession of the rental item(s). Insurance is
          not available for purchase once the rental item(s) have been taken
          possession of. It is solely the responsibility of the Client to
          determine their participation in purchasing an insurance waiver. At no
          time shall any guarantee be made by Kingdom or understood by the
          Client that responsibility for the rented item(s) has been dissolved
          from the Client without the purchase of an insurance waiver. The
          insurance waiver will become null and void in the instance of fraud by
          nature of misrepresentation. The insurance waiver does not cover any
          rental item not returned to the proper location, nor any items left in
          the guest&apos;s room. The insurance waiver is only for items rented
          from Kingdom Strollers and does not include any personal items of the
          guest. The insurance waiver terminates at the designated return time
          listed in the reservation; items not returned by the designated time
          will no longer be covered by the insurance waiver.
        </LP>
        <LP>
          Insurance shall be granted to the Client reserving item(s) from
          Kingdom for which an insurance waiver was purchased. This insurance
          shall at no time be transferable to any individual other than the
          Client, and is only in effect and enforceable while in the possession
          of the Client. The Client will forfeit any claim to the benefit of
          insurance purchased if the Client relinquishes control or possession
          of the rented item(s) at any time to any individual who is not part of
          the original rental agreement.
        </LP>
        <Lead>
          An insurance waiver purchased by the Client shall be null and void in
          the following instances:
        </Lead>
        <UList>
          <li>
            The Client abandons rental items during the course of the rental
            agreement period.
          </li>
          <li>
            The Client disassembles or removes parts from the rental items that
            are not intended to be removed as part of normal operation.
          </li>
          <li>
            The Client uses the rental item for anything other than its intended
            purpose.
          </li>
          <li>
            The Client fails to communicate changes to the agreed-upon delivery
            or pick-up locations.
          </li>
          <li>
            The Client fails to notify Kingdom Strollers immediately upon
            discovery of a stolen item (within 2 hours of discovery).
          </li>
          <li>
            The Client fails to file a report of a lost / stolen item with the
            theme park or resort where the loss occurred within 2 hours of the
            discovery.
          </li>
        </UList>
        <Lead>Insurance does NOT cover the following instances:</Lead>
        <UList>
          <li>
            Rental item(s) abandoned, left behind, or forgotten at a theme park,
            shopping center, or other facility.
          </li>
          <li>
            Rental item(s) abandoned, left behind, or forgotten in a bus, car,
            taxi, or other form of transportation.
          </li>
          <li>
            Rental item(s) left unsecured at a hotel, resort, or vacation home.
          </li>
          <li>Damage done to the equipment of others.</li>
          <li>
            Damage or discoloration of stroller fabric from the application of
            sunscreen, suntan lotions, or sprays.
          </li>
          <li>Marker or pen markings on the interior of the stroller.</li>
          <li>
            Loss of shoulder pads, shoulder straps, lap restraints, and/or any
            latching equipment if the Client has removed them.
          </li>
        </UList>
        <LP>
          <B>Please note:</B> Insurance does not cover the replacement of a
          rental item due to vomit or bathroom accidents, nor replacements for
          wet strollers caused by rain or other incidents outside of
          Kingdom&apos;s control. Kingdom Strollers offers free rain covers to
          protect strollers from the elements. A $10 replacement fee will be
          charged for replacing a wet stroller and is solely based on the
          availability of the model.
        </LP>
        <LP>
          In the event that a rented item is thought to be stolen, the Client
          must obtain a report from theme park security and provide a copy to
          Kingdom. If the event happens outside of a theme park, the Client must
          file a report with the local police department and provide a copy to
          Kingdom.
        </LP>
        <LP>
          Any incident of suspected theft must be reported to both theme park
          security or local police and to Kingdom within 2 hours of the
          incident. This notification must be in the form of email. Any incident
          not reported in a timely manner will void the insurance purchased for
          the rented item(s).
        </LP>
        <LP>
          Purchased insurance is a transfer of liability from the Client to
          Kingdom for the replacement cost of the item for which the insurance
          was purchased. Insurance does not guarantee a replacement item in the
          event the item was stolen or lost. To replace an item stolen or lost,
          a new rental reservation must be placed.
        </LP>
      </>
    ),
  },
  {
    id: 'sec-cancellation',
    toc: 'Cancellation',
    heading: 'Cancellation',
    body: (
      <LP>
        A full refund will be issued for all cancellations made 48 hours or more
        prior to your rental date. All cancellations made within the 48-hour
        window will be charged the minimum rental fee of $40 for a single
        stroller and $50 for a double stroller. Refunds for reservations made
        more than 120 days before the cancellation date will be refunded via
        PayPal or by check.
      </LP>
    ),
  },
  {
    id: 'sec-liability',
    toc: 'Liability Waiver',
    heading: 'Liability Waiver',
    body: (
      <>
        <LP>
          By accepting and using the rental equipment, the Client and any user
          acknowledge that the rental equipment is being used at their own risk.
          Kingdom hereby makes no warranties with respect to the rental
          equipment, and Kingdom hereby specifically disclaims all warranties,
          whether express or implied.
        </LP>
        <LP>
          Kingdom and its employees, owners, and affiliates will not be
          responsible for accidents, injuries, or damages caused directly or
          indirectly by the use or misuse of the rental equipment.
        </LP>
        <LP>
          The Client and any user agree to hold harmless Kingdom and its
          employees, owners, and affiliates from any liability, injury, death,
          property loss, or damage which may result directly or indirectly from
          the use of the rental equipment.
        </LP>
        <LP>This Agreement is governed by the laws of the State of Florida.</LP>
      </>
    ),
  },
  {
    id: 'sec-sms',
    toc: 'SMS Consent',
    heading: 'SMS Consent',
    body: (
      <OList>
        <li>
          By agreeing to the SMS and Privacy Policy, you consent to receive SMS
          messages from Kingdom Strollers regarding your equipment delivery and
          return status updates.
        </li>
        <li>
          You can cancel the SMS service at any time. Just text “STOP” to the
          short code. After you send the message “STOP” to us, we will send an
          SMS to confirm that you have been unsubscribed, and you will no longer
          receive SMS messages from us. If you want to join again, please email
          Kingdom Strollers&apos; offices requesting to be subscribed, and we
          will start sending SMS messages to you again.
        </li>
        <li>
          If you are experiencing issues with the messaging program, you can
          reply with the keyword HELP for more assistance, or get help directly
          at {CONTACT_EMAIL}.
        </li>
        <li>Carriers are not liable for delayed or undelivered messages.</li>
        <li>
          As always, message and data rates may apply for any messages sent to
          you from us and to us from you. If you have any questions about your
          text or data plan, it is best to contact your wireless provider.
        </li>
        <li>
          If you have any questions regarding privacy, please read our privacy
          policy below.
        </li>
      </OList>
    ),
  },
  {
    id: 'sec-privacy',
    toc: 'Privacy Policy',
    heading: 'Privacy Policy',
    body: (
      <>
        <LP>
          This SMS Privacy Policy (“Policy”) explains how Kingdom Strollers LLC
          (“we,” “us,” or “our”) collects, uses, and protects your information
          when you opt in to receive SMS text messages from us.
        </LP>
        <PrivacyHead>1. Consent</PrivacyHead>
        <LP>
          By providing your mobile phone number and opting in to our SMS
          messaging program, you expressly consent to receive recurring
          automated marketing text messages from us. These messages may be sent
          using an automatic telephone dialing system or artificial /
          pre-recorded voice to the mobile number you provide. Your consent to
          receive these messages is not required as a condition of purchasing
          any goods or services.
        </LP>
        <PrivacyHead>2. Message Frequency</PrivacyHead>
        <LP>
          The number of SMS messages you receive will vary based on your
          interaction with our services and your notification preferences.
          Standard message and data rates may apply. Message frequency varies.
        </LP>
        <PrivacyHead>3. Information Collection &amp; Use</PrivacyHead>
        <LP sx={{ mb: 1 }}>
          When you opt in to receive our SMS messages, we collect and store your
          mobile phone number, your opt-in date and time, your name (if
          provided), your SMS messaging history with us, and your interaction
          with our messages. We use this information to:
        </LP>
        <UList>
          <li>Send you the SMS messages you&apos;ve requested</li>
          <li>Manage your messaging preferences</li>
          <li>Improve our services</li>
          <li>Comply with legal requirements</li>
          <li>Protect against fraud and abuse</li>
        </UList>
        <PrivacyHead>4. Information Sharing</PrivacyHead>
        <LP>
          We do not sell, rent, or share your personal information with
          unaffiliated third parties for their marketing purposes. We may share
          your information with service providers who assist in operating our
          SMS program, legal authorities when required by law, and affiliated
          companies as part of our business operations.
        </LP>
        <PrivacyHead>5. Security</PrivacyHead>
        <LP>
          We implement reasonable security measures to protect your information
          from unauthorized access, disclosure, or misuse. However, no data
          transmission or storage system is 100% secure.
        </LP>
        <PrivacyHead>6. Changes to This Policy</PrivacyHead>
        <LP>
          We reserve the right to modify this Policy at any time. We will notify
          you of any material changes through SMS or other reasonable means.
        </LP>
        <PrivacyHead>7. Carriers Supported</PrivacyHead>
        <LP>
          Our SMS program is supported by all major U.S. carriers, including but
          not limited to AT&amp;T, T-Mobile, Verizon, Sprint, and their
          affiliates.
        </LP>
        <PrivacyHead>8. Contact Us</PrivacyHead>
        <LP>
          If you have questions about this Policy or our SMS program, please
          contact Kingdom Strollers at{' '}
          <TextLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</TextLink>.
        </LP>
        <PrivacyHead>9. Additional Terms</PrivacyHead>
        <LP>
          Message and data rates may apply. Carriers are not liable for delayed
          or undelivered messages. SMS messages are distributed via third-party
          mobile network providers and, therefore, we cannot guarantee delivery.
          This Policy is governed by and construed in accordance with the laws
          of the State of Florida and the United States, without regard to its
          conflict-of-law principles. By participating in our SMS program, you
          represent that you are 18 years of age or older and are authorized to
          agree to this Policy.
        </LP>
      </>
    ),
  },
];
